import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Plus, Check, Package2, AlertCircle, ChevronDown, ChevronRight, Minus } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';
import { ProductPhotoService } from '../../services/productPhotoService';

interface StockItem {
  id: string;
  name: string;
  acquisition_cost: number;
  price: number;
  quantity: number;
  category_id: string;
  category_name?: string;
  description?: string;
}

interface StockCategory {
  id: string;
  name: string;
  items?: StockItem[];
}

interface SelectInventoryItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (product: StockItem) => Promise<void>;
  onUpdateQuantity?: (product: StockItem, newQuantity: number) => Promise<void>;
  currentBookingProducts?: Array<{
    product: {
      id: string;
      name: string;
      quantity: number;
    };
  }>;
}

export default function SelectInventoryItemModal({ 
  isOpen, 
  onClose,
  onAdd,
  onUpdateQuantity,
  currentBookingProducts = []
}: SelectInventoryItemModalProps) {
  
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [categories, setCategories] = useState<StockCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});

  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const loadData = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoading(true);
      setError(null);

      // Fetch stock items with categories
      const { data: items, error: itemsError } = await supabase
        .from('stock_items')
        .select(`
          id,
          name,
          acquisition_cost,
          price,
          quantity,
          category_id,
          description,
          stock_categories!inner(name)
        `)
        .eq('business_id', selectedBusiness.id)
        .order('name');

      if (itemsError) throw itemsError;

      // Transform the data to include category names
      const transformedItems: StockItem[] = (items || []).map(item => ({
        id: item.id,
        name: item.name,
        acquisition_cost: item.acquisition_cost || 0,
        price: item.price || 0,
        quantity: item.quantity || 0,
        category_id: item.category_id,
        category_name: item.stock_categories?.name || 'Uncategorized',
        description: item.description
      }));

      setStockItems(transformedItems);

      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('stock_categories')
        .select('id, name')
        .eq('business_id', selectedBusiness.id)
        .order('name');

      if (categoriesError) throw categoriesError;

      // Group items by category
      const categoriesWithItems: StockCategory[] = (categoriesData || []).map(category => ({
        id: category.id,
        name: category.name,
        items: transformedItems.filter(item => item.category_id === category.id)
      }));

      setCategories(categoriesWithItems);

      // Load product photos
      const itemIds = transformedItems.map(item => item.id);
      if (itemIds.length > 0) {
        try {
          const photoMap = await ProductPhotoService.getPhotosByStockItemIds(itemIds);
          
          // Convert to the format expected by the component
          const photoUrls: Record<string, string> = {};
          Object.entries(photoMap).forEach(([itemId, photo]) => {
            photoUrls[itemId] = photo.photo_url;
          });
          
          setProductPhotos(photoUrls);
        } catch (photoError) {
          console.error('Error loading product photos:', photoError);
          // Don't fail the whole load if photos fail
        }
      }

    } catch (err) {
      console.error('Error loading inventory data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load inventory items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
      setIsAnimating(true);
      // Clear selected items when modal opens to reflect current booking state
      setSelectedItems(new Set());
    } else {
      setIsAnimating(false);
    }
  }, [isOpen, selectedBusiness?.id]);

  const filteredCategories = categories.map(category => ({
    ...category,
    items: category.items?.filter(item =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase())
    ) || []
  })).filter(category => category.items.length > 0);

  // Get current booking quantity for an item
  const getCurrentBookingQuantity = (itemId: string): number => {
    const bookingProduct = currentBookingProducts.find(p => p.product.id === itemId);
    return bookingProduct?.product.quantity || 0;
  };

  const handleAddItem = async (item: StockItem) => {
    if (item.quantity <= 0) return; // Prevent adding out-of-stock items
    
    try {
      await onAdd(item);
      setSelectedItems(prev => new Set(prev).add(item.id));
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  const handleUpdateQuantity = async (item: StockItem, newQuantity: number) => {
    if (!onUpdateQuantity) return;
    
    try {
      await onUpdateQuantity(item, newQuantity);
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  // Component for item icon - shows photo if available, otherwise blue icon
  const ItemIcon = ({ item }: { item: StockItem }) => {
    const photoUrl = productPhotos[item.id];
    
    if (photoUrl) {
      return (
        <div className="h-10 w-10 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
          <img 
            src={photoUrl} 
            alt={item.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, show fallback icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = '<div class="h-5 w-5 text-blue-600"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29,7 12,12 20.71,7"/><line x1="12" x2="12" y1="22" y2="12"/></svg></div>';
                parent.className = "h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0";
              }
            }}
          />
        </div>
      );
    }
    
    return (
      <div className="h-10 w-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
        <Package2 className="h-5 w-5" />
      </div>
    );
  };

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100010]`}
      onClick={onClose}
      style={{
        // Ensure modal appears above CreateMobileBookingModal
        zIndex: 100010,
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        isolation: 'isolate',
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="bg-gray-900 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Package2 className="w-6 h-6" />
            <h2 className="text-lg font-semibold">Select Inventory Item</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

          {/* Search */}
          <div className="bg-white border-b border-gray-200 p-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search inventory items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
            />
          </div>
        </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-gray-50">
            {error && (
              <div className="m-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  {error}
                </div>
                <button
                  onClick={loadData}
                  className="mt-2 text-sm font-medium text-red-700 hover:text-red-900 underline"
                >
                  Try again
                </button>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                  <p className="mt-4 text-sm text-gray-600">Loading inventory...</p>
                </div>
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Package2 className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchQuery ? 'No matching products' : 'No products available'}
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {searchQuery ? 'Try adjusting your search terms' : 'Add products to get started'}
                </p>
              </div>
          ) : (
            <div className="p-6 space-y-4">
              {filteredCategories.map(category => (
                <div key={category.id} className="border border-gray-200 rounded-lg">
                  <button
                    onClick={() => toggleCategoryExpansion(category.id)}
                    className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <Package2 className="w-4 h-4 text-gray-600" />
                      <span className="font-medium text-gray-900">{category.name}</span>
                      <span className="text-sm text-gray-500">({category.items?.length || 0} items)</span>
                    </div>
                    {expandedCategories.has(category.id) ? (
                      <ChevronDown className="w-4 h-4 text-gray-600" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-600" />
                    )}
                  </button>
                  
                  {expandedCategories.has(category.id) && (
                    <div className="border-t border-gray-200">
                      {category.items?.map(item => {
                        const isSelected = selectedItems.has(item.id);
                        const isOutOfStock = item.quantity <= 0;
                        const currentBookingQty = getCurrentBookingQuantity(item.id);
                        const isInBooking = currentBookingQty > 0;
                        const availableStock = item.quantity - currentBookingQty;
                        
                        return (
                          <div key={item.id} className="p-4 border-b border-gray-100 last:border-b-0">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3 flex-1">
                                <ItemIcon item={item} />
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3 mb-1">
                                    <h4 className={`font-medium ${isOutOfStock ? 'text-gray-400' : 'text-gray-900'}`}>
                                      {item.name}
                                    </h4>
                                    {isOutOfStock && (
                                      <span className="px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-full">
                                        Out of Stock
                                      </span>
                                    )}
                                  </div>
                                  {item.description && (
                                    <p className={`text-sm mb-1 ${isOutOfStock ? 'text-gray-400' : 'text-gray-600'}`}>
                                      {item.description}
                                    </p>
                                  )}
                                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                                    <span>Cost: ${item.acquisition_cost.toFixed(2)}</span>
                                    <span className={availableStock <= 5 ? 'text-orange-600 font-medium' : ''}>
                                      Stock: {availableStock} available
                                    </span>
                                    {isInBooking && (
                                      <span className="text-blue-600 font-medium">
                                        In booking: {currentBookingQty}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="ml-4 flex items-center space-x-3">
                                <div className="text-right">
                                  <div className={`text-lg font-semibold ${isOutOfStock ? 'text-gray-400' : 'text-gray-900'}`}>
                                    ${item.price.toFixed(2)}
                                  </div>
                                </div>
                                
                                {isInBooking ? (
                                  // Quantity controls for items already in booking
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => handleUpdateQuantity(item, Math.max(0, currentBookingQty - 1))}
                                      className="w-8 h-8 rounded-lg bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 transition-colors"
                                    >
                                      <Minus className="h-3 w-3" />
                                    </button>
                                    <span className="w-8 text-center font-medium">{currentBookingQty}</span>
                                    <button
                                      onClick={() => handleUpdateQuantity(item, currentBookingQty + 1)}
                                      disabled={availableStock <= 0}
                                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                                        availableStock <= 0
                                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                                      }`}
                                    >
                                      <Plus className="h-3 w-3" />
                                    </button>
                                  </div>
                                ) : (
                                  // Add button for items not in booking
                                  <button
                                    onClick={() => handleAddItem(item)}
                                    disabled={isSelected || isOutOfStock || availableStock <= 0}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                      isSelected
                                        ? 'bg-green-100 text-green-700 cursor-not-allowed'
                                        : isOutOfStock || availableStock <= 0
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                  >
                                    {isSelected ? (
                                      <>
                                        <Check className="h-3.5 w-3.5 mr-1.5" />
                                        Added
                                      </>
                                    ) : (
                                      'Add Item'
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return createPortal(modalContent, document.body);
}
